<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useMessageContext } from '../provider.js';
import BaseBubble from './Base.vue';
import FormattedContent from './Text/FormattedContent.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';

const { content, conversationId, id, contentAttributes } = useMessageContext();
const { t } = useI18n();
const store = useStore();

const currentUser = useMapGetter('getCurrentUser');
const currentChat = useMapGetter('getSelectedChat');

const sendAsPublic = computed(() => !!contentAttributes.value?.sendAsPublic);
const buttons = computed(() => contentAttributes.value?.buttons || []);
const hasButtons = computed(() => buttons.value.length > 0);
const expectPrivate = computed(() => !sendAsPublic.value);

const sendingLabel = ref(null);

function messagesAfterCta() {
  const messages = currentChat.value?.messages || [];
  const ctaMessage = messages.find(m => m.id === id.value);
  const ctaCreatedAt = ctaMessage?.created_at || 0;
  return messages.filter(
    msg =>
      msg.id !== id.value &&
      msg.content_type !== 'cta_suggestion' &&
      msg.message_type === 1 &&
      msg.status !== 'failed' &&
      msg.created_at >= ctaCreatedAt
  );
}

const acceptedButtonTitle = computed(() => {
  if (!hasButtons.value) return null;
  const after = messagesAfterCta();
  const found = buttons.value.find(btn =>
    after.some(
      msg => msg.private === expectPrivate.value && msg.content === btn.title
    )
  );
  return found?.title || null;
});

const isAccepted = computed(() => {
  if (hasButtons.value) return !!acceptedButtonTitle.value;
  const after = messagesAfterCta();
  return after.some(
    msg => msg.private === expectPrivate.value && msg.content === content.value
  );
});

const isSending = computed(() => {
  const messages = currentChat.value?.messages || [];
  return messages.some(
    msg =>
      msg.id !== id.value &&
      msg.status === 'progress' &&
      msg.private === expectPrivate.value &&
      msg.content === (sendingLabel.value || content.value)
  );
});

const acceptLabel = computed(() =>
  sendAsPublic.value
    ? t('CONVERSATION.CTA_SUGGESTION.ACCEPT')
    : t('CONVERSATION.CTA_SUGGESTION.REPLY')
);

async function sendOption(message) {
  if (isSending.value || isAccepted.value) return;
  sendingLabel.value = message;
  try {
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: conversationId.value,
      message,
      private: expectPrivate.value,
      sender: currentUser.value,
    });
    emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
    emitter.emit(BUS_EVENTS.MESSAGE_SENT);
    useTrack(CONVERSATION_EVENTS.SENT_MESSAGE);
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || t('CONVERSATION.MESSAGE_ERROR');
    useAlert(errorMessage);
  } finally {
    sendingLabel.value = null;
  }
}
</script>

<template>
  <BaseBubble
    class="px-4 py-3"
    data-bubble-name="cta-suggestion"
    :class="{ 'opacity-60': isAccepted }"
  >
    <div class="flex flex-col gap-3">
      <FormattedContent v-if="content" :content="content" />

      <!-- Multi-button mode -->
      <template v-if="hasButtons">
        <div v-if="!isAccepted" class="flex flex-col gap-1.5">
          <Button
            v-for="btn in buttons"
            :key="btn.title"
            xs
            ghost
            class="!text-white/90 hover:!bg-white/20 !justify-start"
            icon="i-lucide-circle-check"
            :label="btn.title"
            :is-loading="sendingLabel === btn.title"
            :disabled="isSending && sendingLabel !== btn.title"
            @click="sendOption(btn.title)"
          />
        </div>
        <span v-else class="flex items-center gap-1.5 text-xs text-white/60">
          <Icon icon="i-lucide-check" class="size-3" />
          {{ acceptedButtonTitle }}
        </span>
      </template>

      <!-- Single-option mode -->
      <template v-else>
        <div v-if="!isAccepted">
          <Button
            xs
            ghost
            class="!text-white/90 hover:!bg-white/20"
            icon="i-lucide-circle-check"
            :label="acceptLabel"
            :is-loading="isSending"
            @click="sendOption(content)"
          />
        </div>
        <span v-else class="flex items-center gap-1.5 text-xs text-white/60">
          <Icon icon="i-lucide-check" class="size-3" />
          {{ t('CONVERSATION.CTA_SUGGESTION.SENT') }}
        </span>
      </template>
    </div>
  </BaseBubble>
</template>
